import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2, ChevronRight, Sun, Snowflake, CloudRain, Calendar, PiggyBank, Heart, Baby, Sparkles } from 'lucide-react';
import axios from 'axios';
import ActivityCard from '../components/ActivityCard';
import BottomNav from '../components/BottomNav';
import { useLanguage } from '../contexts/LanguageContext';

const API = process.env.REACT_APP_BACKEND_URL;

const collectionIcons = {
  sun: Sun,
  snowflake: Snowflake,
  'cloud-rain': CloudRain,
  'calendar-days': Calendar,
  'piggy-bank': PiggyBank,
  heart: Heart,
  baby: Baby,
  sparkles: Sparkles,
};

const CollectionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [collection, setCollection] = useState(null);
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    const fetchCollection = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/collections/${id}?page=${page}&limit=10`, { withCredentials: true });
        setCollection(res.data.collection);
        if (page === 1) {
          setActivities(res.data.activities);
        } else {
          setActivities(prev => [...prev, ...res.data.activities]);
        }
        setTotalPages(res.data.pages);
      } catch (err) {
        console.error('Failed to load collection:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCollection();
  }, [id, page]);

  const getName = (obj) => obj?.[language] || obj?.en || '';

  const handleSaveToggle = (activityId, saved) => {
    setActivities(prev => prev.map(a => a.id === activityId ? { ...a, is_saved: saved } : a));
  };

  const IconComp = collection ? (collectionIcons[collection.icon] || Sparkles) : Sparkles;

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      {/* Header */}
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
          </button>
          {collection && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${collection.color}15` }}>
                <IconComp className="w-5 h-5" style={{ color: collection.color }} />
              </div>
              <div>
                <h1 className="text-lg font-semibold text-[#2C302E] font-['Outfit']">{getName(collection.name)}</h1>
                <p className="text-xs text-[#666B67]">{collection.activity_count} {t('saved_activities')}</p>
              </div>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6">
        {/* Collection description */}
        {collection && (
          <p className="text-[#666B67] mb-6" data-testid="collection-description">{getName(collection.description)}</p>
        )}

        {loading && activities.length === 0 ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-[#666B67]">{t('discover_no_activities')}</p>
          </div>
        ) : (
          <>
            <div className="grid gap-6">
              {activities.map((activity, index) => (
                <div key={activity.id} className={`fade-in-up fade-in-up-${(index % 5) + 1}`} style={{ opacity: 0 }}>
                  <ActivityCard activity={activity} onSaveToggle={handleSaveToggle} />
                </div>
              ))}
            </div>
            {page < totalPages && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setPage(prev => prev + 1)}
                  disabled={loading}
                  className="btn-secondary inline-flex items-center gap-2"
                  data-testid="load-more-btn"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                    <>{t('discover_load_more')} <ChevronRight className="w-4 h-4" /></>
                  )}
                </button>
              </div>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

const CollectionsList = () => {
  const navigate = useNavigate();
  const { language, t } = useLanguage();
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSeason, setCurrentSeason] = useState('');

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const res = await axios.get(`${API}/api/collections`);
        setCollections(res.data.collections || []);
        setCurrentSeason(res.data.current_season || '');
      } catch (err) {
        console.error('Failed to load collections:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchCollections();
  }, []);

  const getName = (obj) => obj?.[language] || obj?.en || '';
  const getDesc = (obj) => obj?.[language] || obj?.en || '';

  const activeCollections = collections.filter(c => c.is_active);
  const seasonalCollections = activeCollections.filter(c => c.is_seasonal);
  const curatedCollections = activeCollections.filter(c => !c.is_seasonal);

  return (
    <div className="min-h-screen bg-[#F9F8F6] pb-24">
      <header className="sticky top-0 z-40 glass border-b border-[#2C302E]/5">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 rounded-full bg-white border border-[#2C302E]/10 flex items-center justify-center"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5 text-[#2C302E]" />
          </button>
          <h1 className="text-xl font-semibold text-[#2C302E] font-['Outfit']">{t('collections_page_title')}</h1>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-8">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#E07A5F]" />
          </div>
        ) : (
          <>
            {/* Seasonal */}
            {seasonalCollections.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit'] mb-4">{t('collections_seasonal')}</h2>
                <div className="grid gap-4">
                  {seasonalCollections.map((col) => {
                    const IconComp = collectionIcons[col.icon] || Sparkles;
                    return (
                      <button
                        key={col.id}
                        onClick={() => navigate(`/collections/${col.id}`)}
                        className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-[#2C302E]/5 text-left card-hover"
                        data-testid={`collection-card-${col.id}`}
                      >
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${col.color}15` }}>
                          <IconComp className="w-7 h-7" style={{ color: col.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#2C302E]">{getName(col.name)}</h3>
                          <p className="text-sm text-[#666B67] line-clamp-1 mt-0.5">{getDesc(col.description)}</p>
                          <p className="text-xs text-[#666B67] mt-1">{col.activity_count} {t('saved_activities')}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#666B67] flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Curated */}
            {curatedCollections.length > 0 && (
              <section>
                <h2 className="text-lg font-semibold text-[#2C302E] font-['Outfit'] mb-4">{t('collections_curated')}</h2>
                <div className="grid gap-4">
                  {curatedCollections.map((col) => {
                    const IconComp = collectionIcons[col.icon] || Sparkles;
                    return (
                      <button
                        key={col.id}
                        onClick={() => navigate(`/collections/${col.id}`)}
                        className="flex items-center gap-4 p-5 bg-white rounded-2xl border border-[#2C302E]/5 text-left card-hover"
                        data-testid={`collection-card-${col.id}`}
                      >
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${col.color}15` }}>
                          <IconComp className="w-7 h-7" style={{ color: col.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-[#2C302E]">{getName(col.name)}</h3>
                          <p className="text-sm text-[#666B67] line-clamp-1 mt-0.5">{getDesc(col.description)}</p>
                          <p className="text-xs text-[#666B67] mt-1">{col.activity_count} {t('saved_activities')}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[#666B67] flex-shrink-0" />
                      </button>
                    );
                  })}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export { CollectionDetail, CollectionsList };
